import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation, NavLink } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../Firebase/firebaseconfig';
import logoSrc from '../Assets/Square_logo.jpeg';
import Analytics from './Analytics';
import {
  LayoutDashboard, Monitor, MonitorCog,
  PackagePlus, PackageOpen,
  Receipt, ClipboardList, FileText, FileSearch, LogOut, Menu, X,
  Cpu,
} from 'lucide-react';
import './AdminDashboard.css';

const NAV_ITEMS = [
  { label: 'Dashboard',        path: '/admin/dashboard',                exact: true, Icon: LayoutDashboard },
  { label: 'Add PC',           path: '/admin/dashboard/add-pc',                      Icon: Monitor },
  { label: 'Edit PC',          path: '/admin/dashboard/edit-pc',                     Icon: MonitorCog },
  { label: 'Add Component',    path: '/admin/dashboard/add-component',               Icon: PackagePlus },
  { label: 'Edit Component',   path: '/admin/dashboard/edit-component',              Icon: PackageOpen },
  { label: 'Add Desktop',      path: '/admin/dashboard/add-desktop',                 Icon: Cpu },
  { label: 'Edit Desktop',     path: '/admin/dashboard/edit-desktop',                Icon: MonitorCog },
  { label: 'Generate Bill',    path: '/admin/dashboard/generate-bill',               Icon: Receipt },
  { label: 'View Bills',       path: '/admin/dashboard/view-bills',                  Icon: ClipboardList },
  { label: 'Create Quote',     path: '/admin/dashboard/generate-quote',              Icon: FileText },
  { label: 'View Quotes',      path: '/admin/dashboard/view-quotes',                 Icon: FileSearch },
];

function DashboardHome() {
  return (
    <div className="dash-home">
      <h2 className="dash-home-title">Admin Dashboard</h2>
      <p className="dash-home-sub">Visitor analytics overview</p>
      <Analytics />
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) {
        navigate('/admin', { replace: true });
      } else {
        const loginAt = Number(localStorage.getItem('adminLoginAt') || 0);
        if (!loginAt || Date.now() - loginAt > WEEK_MS) {
          localStorage.removeItem('adminLoginAt');
          signOut(auth);
        } else {
          setUser(u);
        }
      }
      setChecking(false);
    });
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleLogout = async () => {
    localStorage.removeItem('adminLoginAt');
    await signOut(auth);
    navigate('/admin', { replace: true });
  };

  if (checking) {
    return (
      <div className="admin-dash-checking">
        <div className="admin-dash-spinner" />
      </div>
    );
  }

  const isDashHome = location.pathname === '/admin/dashboard' || location.pathname === '/admin/dashboard/';

  return (
    <div className="admin-layout">
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-brand">
          <img src={logoSrc} alt="Square Computers" className="admin-dash-logo" />
          <span>Square Computers</span>
        </div>
        <nav className="admin-sidebar-nav">
          {NAV_ITEMS.map(({ label, path, exact, Icon }) => (
            <NavLink
              key={path}
              to={path}
              end={exact}
              className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <Icon size={17} strokeWidth={1.8} className="admin-nav-icon-svg" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="admin-sidebar-footer">
          <span className="admin-sidebar-email">{user?.email || user?.phoneNumber}</span>
          <button onClick={handleLogout} className="admin-logout-btn">
            <LogOut size={14} style={{ marginRight: 5, verticalAlign: 'middle' }} />
            Logout
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="admin-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <div className="admin-content">
        <header className="admin-content-header">
          <button className="admin-hamburger" onClick={() => setSidebarOpen(o => !o)} aria-label="Menu">
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <span className="admin-content-title">Admin Panel</span>
        </header>
        <main className="admin-content-main">
          {isDashHome ? <DashboardHome /> : <Outlet />}
        </main>
      </div>
    </div>
  );
}
